import { Autocomplete, darken, lighten, styled, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import { SEARCH_INVENTORY_PARTS } from './queries'
import { useLazyQuery } from '@apollo/client'
import { UseFormReturn } from '@cedarjs/forms'
import { InventoryItem, Product } from 'types/graphql'

// Example usage with the specific mappings for this component:
const installedStatusMapping: Record<string, string> = {
  scheduled: 'Not Specified',
  worn: 'Not Specified',
  failure: 'Not Specified',
  'premature removal': 'Not Specified',
  'maintenance issue': 'Not Specified',
  'life limit item': 'Not Specified',
}

// likely statuses going to be massaged from
const installedStatuses = [
  'Altered',
  'Inspected',
  'Modified',
  'New',
  'Not Specified',
  'Overhauled',
  'Repaired',
  'Serviceable',
  'Other',
]

// likely status going into massage
// New
// Scheduled
// Overhauled
// Other
// Worn
// Serviceable
// Repaired
// Failure
// Inspected
// Premature Removal
// Maintenance Issue
// Life Limit Item
// Not Specified
// Altered
// NEW

const massageDifferentStatuses = (
  status: string,
  statuses: string[],
  defaultValue = 'Other',
  statusMapping: Record<string, string> = {}
) => {
  // First try exact match
  if (statuses.includes(status)) {
    return status
  }

  // Then try case-insensitive match
  const preparedStatuses = statuses.map((s) => s.toLowerCase())
  const preparedStatus = status.toLowerCase()

  if (preparedStatuses.includes(preparedStatus)) {
    // Return the original case from statuses array
    return statuses[preparedStatuses.indexOf(preparedStatus)]
  }

  // Check if there's a mapping for this status
  const mappedStatus = statusMapping[preparedStatus]
  if (mappedStatus) {
    return mappedStatus
  }

  return defaultValue
}

interface InventoryAutocompleteProps {
  formMethods: UseFormReturn<any>
}

const GroupHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: '-8px',
  padding: '4px 10px',
  color: theme.palette.primary.main,
  backgroundColor: lighten(theme.palette.primary.light, 0.85),
  ...theme.applyStyles('dark', {
    backgroundColor: darken(theme.palette.primary.main, 0.8),
  }),
}))

const GroupItems = styled('ul')({
  padding: 0,
})

const InventoryAutocomplete = ({ formMethods }: InventoryAutocompleteProps) => {
  const [searchValue, setSearchValue] = useState('')
  const [enumeratedInventoryItems, setEnumeratedInventoryItems] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedInventoryItem, setSelectedInventoryItem] = useState(null)
  const [searchInventoryParts] = useLazyQuery(SEARCH_INVENTORY_PARTS)

  // Add debounced search effect
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (searchValue) {
        setLoading(true)
        try {
          const { data } = await searchInventoryParts({
            variables: { search: searchValue },
          })

          setEnumeratedInventoryItems(
            data?.products.reduce((acc, product: Product) => {
              product.inventoryItems.forEach((inventoryItem: InventoryItem) => {
                acc.push({
                  ...inventoryItem,
                  name: product.name,
                  partNumber: product.partNumber,
                })
              })
              return acc
            }, [])
          )
        } catch (error) {
          console.error('Error searching inventory parts:', error)
        } finally {
          setLoading(false)
        }
      } else {
        setEnumeratedInventoryItems([])
      }
    }, 300) // 300ms debounce

    return () => clearTimeout(searchTimeout)
  }, [searchValue, searchInventoryParts])

  // Add effect to create synthetic inventory item when toggling to inventory mode
  useEffect(() => {
    const partNumber = formMethods.getValues('added.partNumber')
    const serialNumber = formMethods.getValues('added.serialNumber')

    if (partNumber && serialNumber) {
      const syntheticItem = {
        name: 'some name',
        partNumber,
        serialNumber,
      }
      setSelectedInventoryItem(syntheticItem)
      setSearchValue(serialNumber)
      setEnumeratedInventoryItems([syntheticItem])
    }
    return () => {
      setSelectedInventoryItem(null)
      setEnumeratedInventoryItems([])
    }
  }, [])

  return (
    <Autocomplete
      options={enumeratedInventoryItems}
      noOptionsText={searchValue ? 'No results found' : 'Search for a part'}
      loading={loading}
      className="col-span-2"
      size="small"
      fullWidth
      value={selectedInventoryItem}
      groupBy={(option) => `${option.name} - ${option.partNumber}`}
      getOptionDisabled={(option) => {
        return option.status === 'IN_USE'
      }}
      getOptionLabel={(option) => option.serialNumber}
      renderGroup={(params) => (
        <li key={params.key}>
          <GroupHeader>{params.group}</GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Search"
          onChange={(e) => setSearchValue(e.target.value)}
          error={
            !!formMethods.formState.errors.added?.partNumber ||
            !!formMethods.formState.errors.added?.serialNumber
          }
          helperText={
            formMethods.formState.errors.added?.partNumber?.message ??
            formMethods.formState.errors.added?.serialNumber?.message
          }
        />
      )}
      onChange={(_, newValue) => {
        setSelectedInventoryItem(newValue)
        if (newValue) {
          formMethods.setValue('added.partNumber', newValue.partNumber)
          formMethods.setValue('added.serialNumber', newValue.serialNumber)
          formMethods.setValue(
            'added.installedStatus',
            massageDifferentStatuses(
              newValue.condition,
              installedStatuses,
              'Other',
              installedStatusMapping
            )
          )
        } else {
          formMethods.setValue('added.partNumber', '')
          formMethods.setValue('added.serialNumber', '')
          formMethods.setValue('added.installedStatus', '')
        }
      }}
    />
  )
}

export default InventoryAutocomplete
